import { RenderPageCtx } from 'datocms-plugin-sdk';
import { Canvas, Spinner, SwitchField } from 'datocms-react-ui';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { readEdgeConfigSingleItem, updateEdgeConfig, requireUserToken, type Keys } from '../utils/edgeConfig';

type PropTypes = {
  ctx: RenderPageCtx,
};

export default function TestModeConfigPage({ ctx }: PropTypes) {
    const [cpbTestMode, setCpbTestMode] = useState<boolean | null>(null);
    const [crtTestMode, setCrtTestMode] = useState<boolean | null>(null);
    const [ctvTestMode, setCtvTestMode] = useState<boolean | null>(null);
    const [ftcTestMode, setFtcTestMode] = useState<boolean | null>(null);
    const [nlcTestMode, setNlcTestMode] = useState<boolean | null>(null);

    useEffect(() => {
        (async() => {
            try {
                const token = requireUserToken(ctx);
                const cpbRes = await readEdgeConfigSingleItem({ key: 'cpb_test_mode', token });
                setCpbTestMode(cpbRes);
                const crtRes = await readEdgeConfigSingleItem({ key: 'crt_test_mode', token });
                setCrtTestMode(crtRes);
                const ctvRes = await readEdgeConfigSingleItem({ key: 'ctv_test_mode', token });
                setCtvTestMode(ctvRes);
                const ftcRes = await readEdgeConfigSingleItem({ key: 'ftc_test_mode', token });
                setFtcTestMode(ftcRes);
                const nlcRes = await readEdgeConfigSingleItem({ key: 'nlc_test_mode', token });
                setNlcTestMode(nlcRes);
            } catch(error) {
                ctx.alert(String(error));
            }
        })()
    // ctx intentionally omitted — it changes reference on every render and would cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Skip update while values are still loading from Edge Config
        if (cpbTestMode === null || crtTestMode === null || ctvTestMode === null || ftcTestMode === null || nlcTestMode === null) return;

        (async() => {
            const numOfTestMode = [cpbTestMode, crtTestMode, ctvTestMode, ftcTestMode, nlcTestMode].filter(value => value).length;
            await ctx.updatePluginParameters({
                testModeSwitcherIsActive: true,
                numOfTestMode: numOfTestMode,
            });
        })();
    // ctx intentionally omitted — it changes reference on every render and would cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cpbTestMode, crtTestMode, ctvTestMode, ftcTestMode, nlcTestMode])

    const onChangeHandler = useCallback(async({
        key,
        value,
        setState
    }:{
        key: Keys,
        value: boolean,
        setState: (val: boolean) => void
    }) => {
            try {
                const token = requireUserToken(ctx);
                const res = await updateEdgeConfig({ key, value, token });
                if(res.status === 'ok') {
                    setState(value);
                }
            } catch(error) {
                ctx.alert(String(error));
            }
    // ctx intentionally omitted — it changes reference on every render and would cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  return (
    <Canvas ctx={ctx}>
      <div
        style={{
            paddingLeft: 'var(--spacing-l)',
            paddingRight: 'var(--spacing-l)'
        }}
    >
        <h2 style={{ fontSize: 'var(--font-size-xl)' }}>Test Mode Config</h2>
        <hr  />
        <p style={{ fontSize: 'var(--font-size-l)' }}>You can update Edge Config for each website.<br/>Please turn on the Test Mode only when an AB test is conducted within the initial screen, such as Hero section, Navbar, and so on.</p>
        <table style={{ textAlign: 'center' }}>
            <thead>
                <tr>
                    <Th>Website</Th>
                    <Th>Test Mode</Th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <Td>CTV</Td>
                    <Td>
                        {
                            ctvTestMode === null ?
                                <Spinner size={48} placement="centered" />
                                :
                                <SwitchField
                                    name="ctv_test_mode_switch"
                                    id="ctv_test_mode_switch"
                                    label=""
                                    value={ctvTestMode}
                                    onChange={() => onChangeHandler(
                                            {
                                                key: 'ctv_test_mode',
                                                value: !ctvTestMode,
                                                setState: setCtvTestMode
                                            }
                                        )}
                                />
                        }
                    </Td>
                </tr>
                <tr>
                    <Td>CRT</Td>
                    <Td>
                        {
                            crtTestMode === null ?
                                <Spinner size={48} placement="centered" />
                                :
                                <SwitchField
                                    name="crt_test_mode_switch"
                                    id="crt_test_mode_switch"
                                    label=""
                                    value={crtTestMode}
                                    onChange={() => onChangeHandler(
                                        {
                                            key: 'crt_test_mode',
                                            value: !crtTestMode,
                                            setState: setCrtTestMode
                                        }
                                    )}
                                />
                        }
                    </Td>
                </tr>
                <tr>
                    <Td>NLC</Td>
                    <Td>
                        {
                            nlcTestMode === null ?
                                <Spinner size={48} placement="centered" />
                                :
                                <SwitchField
                                    name="nlc_test_mode_switch"
                                    id="nlc_test_mode_switch"
                                    label=""
                                    value={nlcTestMode}
                                    onChange={() => onChangeHandler(
                                        {
                                            key: 'nlc_test_mode',
                                            value: !nlcTestMode,
                                            setState: setNlcTestMode
                                        }
                                    )}
                            />
                        }
                    </Td>
                </tr>
                <tr>
                    <Td>CPB</Td>
                    <Td>
                        {
                            cpbTestMode === null ?
                                <Spinner size={48} placement="centered" />
                                :
                                <SwitchField
                                    name="cpb_test_mode_switch"
                                    id="cpb_test_mode_switch"
                                    label=""
                                    value={cpbTestMode}
                                    onChange={() => onChangeHandler(
                                        {
                                            key: 'cpb_test_mode',
                                            value: !cpbTestMode,
                                            setState: setCpbTestMode
                                        }
                                    )}
                                />
                        }
                    </Td>
                </tr>
                <tr>
                    <Td>FTC</Td>
                    <Td>
                        {
                            ftcTestMode === null ?
                                <Spinner size={48} placement="centered" />
                                :
                                <SwitchField
                                    name="ftc_test_mode_switch"
                                    id="ftc_test_mode_switch"
                                    label=""
                                    value={ftcTestMode}
                                    onChange={() => onChangeHandler(
                                        {
                                            key: 'ftc_test_mode',
                                            value: !ftcTestMode,
                                            setState: setFtcTestMode
                                        }
                                    )}
                                />
                        }
                    </Td>
                </tr>
            </tbody>
        </table>
      </div>
    </Canvas>
  );
}

function Th({children}: {children: ReactNode}) {
    return <th style={{padding: '1rem'}}>{children}</th>
};

function Td({children}: {children: ReactNode}) {
    return <td style={{padding: '1rem'}}>{children}</td>
};